import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../redux/store';
import { toAbsoluteUrl } from '../_metronic/helpers/AssetHelpers';
import { ColumnFilterModel } from '../models/common/ColumnFilterModel';
import Tooltip from './Tooltip';

const YENILEME_MS = 2 * 60 * 1000;

interface Props {
    /** public/media altındaki ikon (ör. "media/icons/e-fatura.png"). */
    resim: string
    /** Üzerine gelince ve ekran okuyucuda görünen açıklama. */
    baslik: string
    /** Hedef listenin menü adı: ikon yalnız bu ekrana yetkisi olana görünür. */
    menuAdi: string
    /** Hataya düşmüş kayıt sayısını getiren servis çağrısı. */
    sayiGetir: () => Promise<{ isSuccess: boolean, data: number }>
    /** Tıklayınca açılacak liste ve uygulanacak kolon filtresi. */
    hedef: string
    filtreler: ColumnFilterModel[]
}

/**
 * Üst bardaki e-Fatura / e-İrsaliye ikonu (07.10.2026): zil (BildirimZili) ile aynı çalışır. Uyumsoft'ta hataya
 * düşmüş kayıt sayısı kırmızı rozetle gösterilir. İkon her zaman görünür (Burak, 07.10.2026); rozet yalnız hata varken.
 * Tıklayınca liste "Hata" filtresiyle açılır.
 * Sayı firma değişince, sayfa değişince ve 2 dakikada bir yenilenir; servis hata verirse sessizce bekler.
 */
const EntegrasyonHataIkonu = ({ resim, baslik, menuAdi, sayiGetir, hedef, filtreler }: Props) => {
    const auth = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    const location = useLocation();
    const [adet, setAdet] = useState(0);

    const yetkili = auth.user.user?.data.authorizedMenus?.some(x => x.menuName == menuAdi) ?? false;
    const firmaId = auth.selectedCompany?.companyId;

    const yenile = useCallback(async () => {
        if (!yetkili)
            return;

        try {
            const response = await sayiGetir();

            if (response.isSuccess)
                setAdet(response.data ?? 0);
        } catch {
            // Sessizce bekler; bir sonraki yenilemede tekrar dener.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [yetkili]);

    useEffect(() => {
        yenile();
    }, [yenile, firmaId, location.pathname]);

    useEffect(() => {
        const zamanlayici = setInterval(yenile, YENILEME_MS);
        return () => clearInterval(zamanlayici);
    }, [yenile]);

    if (!yetkili)
        return null;

    return (
        <Tooltip content={adet > 0 ? baslik + ': ' + adet : baslik + ': hata yok'} direction='bottom'>
            <button
                type='button'
                className='btn btn-icon btn-active-light-primary position-relative h-40px me-3'
                style={{ width: 'auto', paddingLeft: 4, paddingRight: 12 }}
                onClick={() => navigate(hedef, { state: { columnFilters: filtreler } })}
                aria-label={`${baslik}: ${adet}`}
            >
                {/* Zil ikonuyla aynı yükseklik (fs-1 ≈ 28px). */}
                <img src={toAbsoluteUrl(resim)} alt={baslik} style={{ height: 28, width: 'auto' }} />
                {adet > 0 && <span className='position-absolute badge badge-danger rounded-pill' style={{ top: 3, right: 0, minWidth: 18, height: 18, fontSize: 10, lineHeight: '18px', padding: '0 5px', color: '#fff' }}>
                    {adet > 999 ? '999+' : adet}
                </span>}
            </button>
        </Tooltip>
    );
}

export default EntegrasyonHataIkonu;
